import React, { useRef } from "react";

//components
import { Alert } from "@performyard/py-components";

//services
import { IStrategyCategory, getStrategyCategories } from "../../api/services";

import AsyncSelectInput from "../Inputs/AsyncSelectInput";
import { Result } from "../Inputs/SelectInput";
import Spacer from "../Spacer";
import Typography from "../Text";

interface StrategyCategoryPickerProps {
	/**
	 * current value of the picker
	 */
	value?: Result | Result[];

	/**
	 * function runs on StrategyCategoryPicker change
	 */
	onChange?: (value: Result | Result[]) => void;

	/**
	 * label for the picker
	 */
	label?: string;

	/**
	 * isMultiSelect determines if the picker is a single select or multi select
	 */
	isMultiSelect?: boolean;

	/**
	 * isClearable determines if the picker is clearable
	 */
	isClearable?: boolean;

	/**
	 * disabled determines if the picker is disabled
	 */
	disabled?: boolean;

	/**
	 * placeholder for the picker
	 */
	placeholder?: string;

	/**
	 * error message to display below the picker
	 */
	errorMessage?: string;
}

const StrategyCategoryPicker = ({
	value,
	onChange,
	label,
	isMultiSelect = false,
	isClearable = false,
	disabled,
	placeholder,
	errorMessage,
}: StrategyCategoryPickerProps) => {
	const currentlyFetchedCategories = useRef<IStrategyCategory[]>([]);

	/* fetchOptions gets called every time the user types in the input or initially opens the dropdown.
	 * It returns a  an array of options needed for the AsyncSelectInput.
	 * In this case we also cache the currently fetched items in a ref so we can filter the list if a user types into the search
	 * and we dont have to fetch the items again.
	 *
	 * In the future we may want to add a searchable endpoint so that we dont pull all items and filter in this way (similar to employees)
	 */
	const fetchOptions = async (inputValue: string) => {
		try {
			if (currentlyFetchedCategories.current.length < 1) {
				const { categories } = await getStrategyCategories();
				const filteredCategories = categories.filter((category) => !category?.deleted);
				currentlyFetchedCategories.current = filteredCategories;
			}

			const fetchedCategories = currentlyFetchedCategories.current.map((user) => {
				return {
					label: user.name,
					value: user._id,
				};
			});

			return inputValue
				? fetchedCategories.filter((category) =>
						category.label.toLowerCase().includes(inputValue.toLowerCase())
					)
				: fetchedCategories;
		} catch (error) {
			throw new Error("Error fetching category strategies");
			return [];
		}
	};

	return (
		<div data-testid="StrategyCategoryPicker">
			{label && (
				<>
					<Typography
						element="label"
						size="paragraph-3"
						weight="semi-bold"
						htmlFor="category-picker"
						color={errorMessage ? "red-6" : undefined}
					>
						{`${label}${errorMessage ? "*" : ""}`}
					</Typography>
					<Spacer height={1} />
				</>
			)}
			<AsyncSelectInput
				inputId="category-picker"
				fetchOptions={fetchOptions}
				value={value}
				onChange={(categoryAsResult) => {
					if (onChange) {
						onChange(categoryAsResult);
					}
				}}
				isMultiSelect={isMultiSelect}
				isClearable={isClearable}
				disabled={disabled}
				placeholder={placeholder}
			/>
			{errorMessage && <Alert type="error" message={errorMessage} />}
		</div>
	);
};

export default StrategyCategoryPicker;
